import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { Check, Crosshair, X } from 'lucide-react';
import { useIntl } from 'react-intl';

import { useComputerUseNotePushMappingContext } from './computer-use-note-push-mapping-context.helpers';

/**
 * A sibling of {@link LinkEhrSectionDropdown} rendered in the section toolbar,
 * template-builder-only. Shows the section's current note-push mapping (stored
 * hint or user override) and opens a dropdown of discovered FIELD candidates.
 *
 * Selecting a candidate writes the CANDIDATE STATE ONLY (a user override that
 * feeds the next delegation brief); the durable hint store is never written
 * from the FE. Renders nothing when no provider is mounted (i.e. everywhere
 * except the template-builder panel).
 */
export const ComputerUseNotePushMappingChip = ({
  heading,
  onOpenChange,
  disabled = false,
}: {
  heading: string;
  onOpenChange: (open: boolean) => void;
  disabled?: boolean;
}) => {
  const intl = useIntl();
  const {
    enabled,
    isHydratedFromStore,
    fieldCandidates,
    getSectionMapping,
    onReassignSection,
  } = useComputerUseNotePushMappingContext();

  if (!enabled) return null;

  const mapping = getSectionMapping(heading);
  const currentSelector = mapping?.selectors[0];
  // Prefer the resolved label of the current target (a stored hint or override)
  // so a saved mapping reads even before on-screen discovery finds a matching
  // field; fall back to the "Map field" nudge only when nothing is mapped.
  const triggerLabel =
    mapping?.label ??
    intl.formatMessage({ id: '+LB9F0yCz7', defaultMessage: 'Map field' });
  // Field candidates only exist after a read-only discovery run scans the app.
  const noCandidates = fieldCandidates.length === 0;

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          onMouseDown={(event) => event.preventDefault()}
          size="xs"
          className="p-1"
          variant="ghost"
          type="button"
          disabled={disabled}
          aria-label={intl.formatMessage({
            id: 'bsJw2Qwyzs',
            defaultMessage: 'Map section to an EHR field',
          })}
          data-testid="note-push-mapping-trigger"
        >
          {triggerLabel}
          <Crosshair size={12} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {noCandidates && isHydratedFromStore ? (
          // Stored-fallback mapping (post-reload, no live flow): nothing is
          // scanning, so say so instead of a permanently "in progress" menu.
          <DropdownMenuItem disabled data-testid="note-push-mapping-stored">
            {intl.formatMessage({
              id: 'L//d+HTdU7',
              defaultMessage: 'Field reassignment is available during a note push',
              description:
                'Note-push mapping chip menu shown for a stored (hydrated) mapping when no live discovery run can supply fields to reassign to',
            })}
          </DropdownMenuItem>
        ) : noCandidates ? (
          // Nothing to reassign to until discovery runs — surface progress
          // rather than an empty menu.
          <DropdownMenuItem disabled data-testid="note-push-mapping-scanning">
            {intl.formatMessage({
              id: 'PQqEQbYrwY',
              defaultMessage: 'Scanning the app for fields…',
              description:
                'Note-push mapping chip menu shown while on-screen field discovery is still running',
            })}
          </DropdownMenuItem>
        ) : (
          fieldCandidates.map((candidate) => {
            const isActive = candidate.selector === currentSelector;
            return (
              <DropdownMenuItem
                key={candidate.selector}
                onMouseDown={(event) => event.preventDefault()}
                onSelect={() => onReassignSection(heading, [candidate.selector])}
                data-testid="note-push-mapping-candidate"
              >
                {candidate.label}
                {isActive && <Check size={14} className="ms-auto" />}
              </DropdownMenuItem>
            );
          })
        )}
        {mapping?.isOverride && (
          <>
            {fieldCandidates.length > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onMouseDown={(event) => event.preventDefault()}
              onSelect={() => onReassignSection(heading, [])}
              data-testid="note-push-mapping-clear"
            >
              <X size={14} />
              {intl.formatMessage({
                id: 'jCx6wFcBzq',
                defaultMessage: 'Clear override',
              })}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
